import {
  type FormEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { ImageUp, TriangleAlert } from "lucide-react";
import { toast } from "sonner";

import {
  type CarrierCompanyProfile,
  type CarrierCompanyProfilePatch,
  useCompanyProfile,
  useUpdateCompanyProfile,
  useUploadCompanyLogo,
} from "@/api/carrier";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useUnsavedChangesGuard } from "@/hooks/use-unsaved-changes-guard";
import { hasHttpStatus } from "@/lib/api-error";
import { cn } from "@/lib/utils";

interface FormState {
  name: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  dispatchEmail: string;
  logoUrl: string;
  addressLine1: string;
  addressLine2: string;
  addressCity: string;
  addressProvince: string;
  addressCountry: string;
  addressPostalCode: string;
}

const CONTACT_FIELDS: Array<{
  key: "contactName" | "contactPhone" | "contactEmail" | "dispatchEmail";
  label: string;
  type?: string;
  inputMode?: "email" | "tel";
}> = [
  { key: "contactName", label: "Contact name" },
  {
    key: "contactPhone",
    label: "Contact phone",
    type: "tel",
    inputMode: "tel",
  },
  {
    key: "contactEmail",
    label: "Contact email",
    type: "email",
    inputMode: "email",
  },
  {
    key: "dispatchEmail",
    label: "Dispatch email",
    type: "email",
    inputMode: "email",
  },
];

const ADDRESS_FIELDS = [
  { key: "addressLine1", label: "Address line 1" },
  { key: "addressLine2", label: "Address line 2" },
  { key: "addressCity", label: "City" },
  { key: "addressProvince", label: "Province" },
  { key: "addressCountry", label: "Country" },
  { key: "addressPostalCode", label: "Postal code" },
] as const;

const IMAGE_TYPES = new Set([
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/svg+xml",
]);

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? "");
      resolve(result.includes(",") ? result.split(",")[1] : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return parts
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? "")
    .join("");
}

export function CompanyProfileForm() {
  const profile = useCompanyProfile();
  const update = useUpdateCompanyProfile();
  const uploadLogo = useUploadCompanyLogo();
  const [form, setForm] = useState<FormState | null>(null);
  const [initialForm, setInitialForm] = useState<FormState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const lastSeenProfile = useRef<CarrierCompanyProfile | null>(null);
  const pendingProfile = useRef<CarrierCompanyProfile | null>(null);
  const submissionLock = useRef(false);
  const uploadLock = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dirty =
    form !== null &&
    initialForm !== null &&
    JSON.stringify(form) !== JSON.stringify(initialForm);
  const profileForbidden = hasHttpStatus(profile.error, 403);

  useUnsavedChangesGuard(
    dirty,
    "Leaving this page will discard your unsaved company-profile changes. Continue?"
  );

  useEffect(() => {
    if (profile.data && lastSeenProfile.current !== profile.data) {
      lastSeenProfile.current = profile.data;
      if (form && dirty) {
        pendingProfile.current = profile.data;
        return;
      }
      const next = profileToForm(profile.data);
      setForm(next);
      setInitialForm(next);
      pendingProfile.current = null;
      return;
    }
    if (!dirty && pendingProfile.current) {
      const next = profileToForm(pendingProfile.current);
      pendingProfile.current = null;
      setForm(next);
      setInitialForm(next);
    }
  }, [dirty, form, profile.data]);

  const setField = (key: keyof FormState) => (value: string) => {
    setForm((current) => (current ? { ...current, [key]: value } : current));
    setError(null);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!form || !initialForm || submissionLock.current || uploadLock.current)
      return;
    const validation = validateForm(form);
    if (validation) {
      setError(validation);
      return;
    }
    const normalized = normalizeForm(form);
    const normalizedInitial = normalizeForm(initialForm);
    const patch = buildPatch(normalized, normalizedInitial);
    if (Object.keys(patch).length === 0) {
      setForm(normalized);
      setInitialForm(normalized);
      toast.info("No changes to save.");
      return;
    }

    submissionLock.current = true;
    setSaving(true);
    setError(null);
    try {
      const saved = await update.mutateAsync(patch);
      const reconciled = profileToForm(saved);
      pendingProfile.current = null;
      setForm(reconciled);
      setInitialForm(reconciled);
      toast.success("Company profile saved.");
    } catch (caught) {
      const response = caught as { response?: { data?: { error?: string } } };
      const message = hasHttpStatus(caught, 403)
        ? "You do not have permission to edit the company profile."
        : (response.response?.data?.error ??
          "Company profile could not be saved.");
      setError(message);
      toast.error(message);
    } finally {
      submissionLock.current = false;
      setSaving(false);
    }
  };

  const handleLogoFile = async (file: File | undefined) => {
    setLogoError(null);
    if (!file || uploadLock.current || submissionLock.current) return;
    if (!IMAGE_TYPES.has(file.type)) {
      setLogoError("Logo must be PNG, JPG, WebP, or SVG.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setLogoError("Logo must be 2 MB or smaller.");
      return;
    }

    uploadLock.current = true;
    setUploading(true);
    try {
      const dataBase64 = await fileToBase64(file);
      const saved = await uploadLogo.mutateAsync({
        fileName: file.name,
        contentType: file.type as
          | "image/png"
          | "image/jpeg"
          | "image/webp"
          | "image/svg+xml",
        dataBase64,
      });
      const logoUrl = saved.logoUrl ?? "";
      if (pendingProfile.current) pendingProfile.current = saved;
      setForm((current) => (current ? { ...current, logoUrl } : current));
      setInitialForm((current) =>
        current ? { ...current, logoUrl } : current
      );
      toast.success("Logo uploaded.");
    } catch (caught) {
      const message = hasHttpStatus(caught, 403)
        ? "You do not have permission to upload a company logo."
        : "Company logo could not be uploaded.";
      setLogoError(message);
      toast.error(message);
    } finally {
      uploadLock.current = false;
      setUploading(false);
    }
  };

  if (profile.isLoading && !form) {
    return <ProfileLoading />;
  }
  if (profile.error && (profileForbidden || !form)) {
    return (
      <div role="alert" className="space-y-3">
        <div className="flex items-start gap-2 text-sm text-destructive">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {profileForbidden
              ? "You do not have permission to view the company profile."
              : "Company profile could not be loaded."}
          </span>
        </div>
        {!profileForbidden && (
          <Button
            type="button"
            variant="outline"
            size="sm-touch"
            onClick={() => void profile.refetch()}
          >
            Try again
          </Button>
        )}
      </div>
    );
  }
  if (!form || !initialForm) return <ProfileLoading />;

  return (
    <form noValidate onSubmit={handleSubmit} className="space-y-6">
      {profile.error && (
        <div
          role="alert"
          className="flex flex-col gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 sm:flex-row sm:items-center sm:justify-between"
        >
          <span>
            Profile refresh failed. Loaded company values remain editable.
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm-touch"
            className="bg-background"
            onClick={() => void profile.refetch()}
          >
            Try again
          </Button>
        </div>
      )}

      <FormSection
        title="Identity"
        description="How your carrier appears to EZX and customers."
      >
        <div className="grid gap-4 sm:grid-cols-[auto_minmax(0,1fr)] sm:items-start">
          <div className="flex items-start gap-3">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border bg-muted">
              {form.logoUrl ? (
                <img
                  src={form.logoUrl}
                  alt="Company logo"
                  className="h-full w-full object-contain"
                />
              ) : (
                <span className="text-lg font-semibold text-muted-foreground">
                  {initials(form.name)}
                </span>
              )}
            </div>
            <div className="space-y-1">
              <Button
                type="button"
                variant="outline"
                size="sm-touch"
                disabled={uploading || saving}
                onClick={() => fileInputRef.current?.click()}
              >
                <ImageUp className="h-4 w-4" />
                {uploading ? "Uploading…" : "Upload logo"}
              </Button>
              <input
                ref={fileInputRef}
                id="cp-logo-file"
                aria-label="Choose company logo file"
                type="file"
                aria-describedby={logoError ? "cp-logo-error" : undefined}
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                className="sr-only"
                disabled={uploading || saving}
                onChange={(event) => {
                  void handleLogoFile(event.target.files?.[0]);
                  event.currentTarget.value = "";
                }}
              />
              <p className="text-[11px] text-muted-foreground">
                PNG, JPG, WebP, or SVG · max 2 MB
              </p>
              {logoError && (
                <p
                  id="cp-logo-error"
                  role="alert"
                  className="max-w-64 text-xs text-destructive"
                >
                  {logoError}
                </p>
              )}
            </div>
          </div>
          <Field
            id="cp-name"
            label="Company name"
            value={form.name}
            disabled={saving}
            onChange={setField("name")}
          />
        </div>

        <details className="group">
          <summary className="flex min-h-11 cursor-pointer items-center text-xs text-muted-foreground transition-colors hover:text-foreground">
            Set logo by URL instead
          </summary>
          <div className="pt-2">
            <Field
              id="cp-logoUrl"
              label="Logo URL"
              type="url"
              inputMode="url"
              value={form.logoUrl}
              disabled={saving}
              onChange={setField("logoUrl")}
              className="max-w-xl"
            />
          </div>
        </details>
      </FormSection>

      <FormSection
        title="Contact"
        description="Where EZX dispatch and support reach you."
      >
        <div className="grid gap-3 sm:grid-cols-2">
          {CONTACT_FIELDS.map((field) => (
            <Field
              key={field.key}
              id={`cp-${field.key}`}
              label={field.label}
              type={field.type}
              inputMode={field.inputMode}
              value={form[field.key]}
              disabled={saving}
              onChange={setField(field.key)}
            />
          ))}
        </div>
      </FormSection>

      <FormSection title="Address" description="Operating or billing address.">
        <div className="grid gap-3 sm:grid-cols-2">
          {ADDRESS_FIELDS.map((field) => (
            <Field
              key={field.key}
              id={`cp-${field.key}`}
              label={field.label}
              value={form[field.key]}
              disabled={saving}
              onChange={setField(field.key)}
            />
          ))}
        </div>
      </FormSection>

      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      {(saving || uploading) && (
        <p role="status" className="text-sm text-muted-foreground">
          {uploading ? "Uploading company logo…" : "Saving company profile…"}
        </p>
      )}

      <div className="flex flex-col-reverse gap-2 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
        <span className="text-xs text-muted-foreground">
          {dirty ? "Unsaved changes" : "No unsaved changes"}
        </span>
        <Button
          type="submit"
          size="touch"
          className="w-full sm:w-auto"
          disabled={saving || uploading || !dirty}
        >
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </form>
  );
}

function FormSection({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-3 border-t pt-4 first:border-t-0 first:pt-0">
      <div>
        <h3 className="text-sm font-semibold">{title}</h3>
        <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
      </div>
      {children}
    </section>
  );
}

function Field({
  id,
  label,
  type,
  inputMode,
  value,
  disabled,
  className,
  onChange,
}: {
  id: string;
  label: string;
  type?: string;
  inputMode?: "email" | "tel" | "url";
  value: string;
  disabled?: boolean;
  className?: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className={cn("space-y-1", className)}>
      <label htmlFor={id} className="text-xs font-medium">
        {label}
      </label>
      <Input
        id={id}
        type={type ?? "text"}
        inputMode={inputMode}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

function ProfileLoading() {
  return (
    <div
      role="status"
      aria-label="Loading company profile"
      className="space-y-3"
    >
      <Skeleton className="h-20" />
      <div className="grid gap-3 sm:grid-cols-2">
        <Skeleton className="h-14" />
        <Skeleton className="h-14" />
        <Skeleton className="h-14" />
        <Skeleton className="h-14" />
      </div>
    </div>
  );
}

function profileToForm(profile: CarrierCompanyProfile): FormState {
  return {
    name: profile.name ?? "",
    contactName: profile.contactName ?? "",
    contactEmail: profile.contactEmail ?? "",
    contactPhone: profile.contactPhone ?? "",
    dispatchEmail: profile.dispatchEmail ?? "",
    logoUrl: profile.logoUrl ?? "",
    addressLine1: profile.address?.line1 ?? "",
    addressLine2: profile.address?.line2 ?? "",
    addressCity: profile.address?.city ?? "",
    addressProvince: profile.address?.province ?? "",
    addressCountry: profile.address?.country ?? "",
    addressPostalCode: profile.address?.postalCode ?? "",
  };
}

function normalizeForm(form: FormState): FormState {
  return Object.fromEntries(
    Object.entries(form).map(([key, value]) => [key, value.trim()])
  ) as unknown as FormState;
}

function validateForm(form: FormState): string | null {
  if (!form.name.trim()) return "Company name is required.";
  for (const [label, value] of [
    ["Contact email", form.contactEmail],
    ["Dispatch email", form.dispatchEmail],
  ]) {
    if (value.trim() && !/^\S+@\S+\.\S+$/.test(value.trim())) {
      return `${label} must be a valid email address.`;
    }
  }
  if (form.logoUrl.trim()) {
    try {
      const url = new URL(form.logoUrl.trim());
      if (url.protocol !== "http:" && url.protocol !== "https:") {
        return "Logo URL must use http or https.";
      }
    } catch {
      return "Logo URL must be a valid web address.";
    }
  }
  return null;
}

function buildPatch(
  form: FormState,
  initial: FormState
): CarrierCompanyProfilePatch {
  const patch: CarrierCompanyProfilePatch = {};
  if (form.name !== initial.name) patch.name = form.name;
  for (const key of [
    "contactName",
    "contactEmail",
    "contactPhone",
    "dispatchEmail",
    "logoUrl",
  ] as const) {
    if (form[key] !== initial[key]) {
      patch[key] = form[key] || null;
    }
  }

  const address: NonNullable<CarrierCompanyProfilePatch["address"]> = {};
  const addressMap = {
    addressLine1: "line1",
    addressLine2: "line2",
    addressCity: "city",
    addressProvince: "province",
    addressCountry: "country",
    addressPostalCode: "postalCode",
  } as const;
  for (const [formKey, apiKey] of Object.entries(addressMap) as Array<
    [keyof typeof addressMap, (typeof addressMap)[keyof typeof addressMap]]
  >) {
    if (form[formKey] !== initial[formKey]) {
      address[apiKey] = form[formKey] || null;
    }
  }
  if (Object.keys(address).length > 0) patch.address = address;
  return patch;
}
